import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Globe, Plus } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { PageHeader } from '@/components/common/PageHeader';
import { DataTable } from '@/components/common/DataTable';
import { Mono, StatusDot, FamilyBadge } from '@/components/common/display';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { CreateAddressDialog } from '@/components/address/CreateAddressDialog';
import { useAddresses } from '@/hooks/use-addresses';
import { stripHostMask } from '@/lib/ip-utils';
import type { AddressListItem } from '@/lib/types';

export interface AddressesSearch {
  q?: string;
}

export const Route = createFileRoute('/_auth/addresses')({
  validateSearch: (search: Record<string, unknown>): AddressesSearch => ({
    q: typeof search.q === 'string' ? search.q : undefined,
  }),
  component: AddressesPage,
});

const STATUS_LABELS: Record<string, string> = {
  active: 'Activo',
  reserved: 'Reservado',
  deprecated: 'Obsoleto',
  dhcp: 'DHCP',
  slaac: 'SLAAC',
  quarantine: 'Cuarentena',
  planned: 'Planificado',
};

const COLUMNS: ColumnDef<AddressListItem>[] = [
  {
    accessorKey: 'address',
    header: 'Dirección',
    cell: ({ row }) => <Mono>{stripHostMask(row.original.address)}</Mono>,
  },
  {
    accessorKey: 'family',
    header: 'Familia',
    cell: ({ row }) => <FamilyBadge family={row.original.family} />,
  },
  {
    accessorKey: 'status',
    header: 'Estado',
    cell: ({ row }) => (
      <span className="flex items-center gap-1.5 text-xs">
        <StatusDot status={row.original.status} />
        {STATUS_LABELS[row.original.status] ?? row.original.status}
      </span>
    ),
  },
  {
    accessorKey: 'hostname',
    header: 'Hostname',
    cell: ({ row }) =>
      row.original.hostname ? (
        <Mono className="text-xs">{row.original.hostname}</Mono>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    accessorKey: 'device_ref',
    header: 'Dispositivo',
    cell: ({ row }) =>
      row.original.device_ref ? (
        <Mono className="text-xs">{row.original.device_ref}</Mono>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
];

function AddressesPage() {
  const { q } = Route.useSearch();
  const { data: allAddresses = [], isLoading, error } = useAddresses();

  const [createOpen, setCreateOpen] = useState(false);
  const [textFilter, setTextFilter] = useState(q ?? '');
  const [familyFilter, setFamilyFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const filtered = useMemo(() => {
    const text = textFilter.trim().toLowerCase();
    return allAddresses.filter((a) => {
      if (familyFilter !== 'all' && String(a.family) !== familyFilter) return false;
      if (statusFilter !== 'all' && a.status !== statusFilter) return false;
      if (text) {
        const haystack = [
          stripHostMask(a.address),
          a.hostname,
          a.device_ref,
          a.status,
        ]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(text)) return false;
      }
      return true;
    });
  }, [allAddresses, familyFilter, statusFilter, textFilter]);

  const toolbar = (
    <>
      <Input
        className="h-9 w-full font-mono text-xs sm:h-7 sm:w-44"
        placeholder="Buscar…"
        value={textFilter}
        onChange={(e) => setTextFilter(e.target.value)}
      />
      <Select value={familyFilter} onValueChange={setFamilyFilter}>
        <SelectTrigger className="h-9 flex-1 text-xs sm:h-7 sm:w-28 sm:flex-none">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todas</SelectItem>
          <SelectItem value="4">IPv4</SelectItem>
          <SelectItem value="6">IPv6</SelectItem>
        </SelectContent>
      </Select>
      <Select value={statusFilter} onValueChange={setStatusFilter}>
        <SelectTrigger className="h-9 flex-1 text-xs sm:h-7 sm:w-32 sm:flex-none">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todos estados</SelectItem>
          <SelectItem value="active">Activo</SelectItem>
          <SelectItem value="reserved">Reservado</SelectItem>
          <SelectItem value="deprecated">Obsoleto</SelectItem>
          <SelectItem value="dhcp">DHCP</SelectItem>
          <SelectItem value="slaac">SLAAC</SelectItem>
        </SelectContent>
      </Select>
    </>
  );

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Direcciones"
        icon={Globe}
        description="Inventario de direcciones host IP"
        actions={
          <Button size="sm" className="h-7 gap-1.5" onClick={() => setCreateOpen(true)}>
            <Plus className="size-3.5" />
            Nueva dirección
          </Button>
        }
      />

      <div className="flex min-h-0 flex-1 flex-col p-4">
        <DataTable
          columns={COLUMNS}
          data={filtered}
          getRowId={(r) => r.id}
          isLoading={isLoading}
          error={error}
          emptyTitle="Sin direcciones"
          emptyDescription="No se encontraron direcciones con los filtros aplicados."
          toolbar={toolbar}
          csvFilename="addresses"
          virtualize
          mobileCard={(a) => (
            <div className="rounded-lg border border-border bg-blaze-elev1 p-3">
              <div className="flex items-center justify-between gap-2">
                <Mono className="text-sm text-foreground">{stripHostMask(a.address)}</Mono>
                <FamilyBadge family={a.family} />
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <StatusDot status={a.status} />
                  {STATUS_LABELS[a.status] ?? a.status}
                </span>
                {a.hostname && <Mono className="text-[11px]">{a.hostname}</Mono>}
                {a.device_ref && <Mono className="text-[11px]">{a.device_ref}</Mono>}
              </div>
            </div>
          )}
        />
      </div>

      <CreateAddressDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}
